/**
 * Phase 0 observability — read-only favorites inventory, once per browser session.
 * Does not read or write favorites; reports counts for pre-migration baseline.
 */
import { useEffect } from 'react';
import { trackEvent } from '@/lib/analytics';
import { readFavoritesCache } from '@/hooks/useFerryFavorites';
import {
  filterCloudSambandFavorites,
  mergeSambandFavoritesForRender,
} from '@/lib/ferryFavoriteResolution';
import type { FavoriteRoute } from '@/hooks/useFavoriteRoutes';

const SESSION_KEY = 'favorites_inventory_logged_v1';
const LOCAL_SAMBAND_KEY = 'favoriteFerrySamband_v2';
const DOCK_KEY = 'favoriteFerryDock';
const COMMUTER_KEY = 'commuter_saved_routes';
const FERRY_FAVORITES_UPDATED_EVENT = 'ferryFavoritesUpdated';
const LOG_DEFER_MS = 3000;

function readLocalSambandCount(): number {
  try {
    const raw = localStorage.getItem(LOCAL_SAMBAND_KEY);
    if (!raw) return 0;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}

function readDockFavoriteExists(): boolean {
  try {
    return !!localStorage.getItem(DOCK_KEY);
  } catch {
    return false;
  }
}

function readCommuterRouteCount(): number {
  try {
    const raw = localStorage.getItem(COMMUTER_KEY);
    if (!raw) return 0;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}

function readLegacyRouteFavorites(): FavoriteRoute[] {
  try {
    const raw = localStorage.getItem(LOCAL_SAMBAND_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is FavoriteRoute =>
        typeof item === 'object'
        && item !== null
        && typeof (item as FavoriteRoute).slug === 'string',
    );
  } catch {
    return [];
  }
}

export function useFavoritesInventorySnapshot(): void {
  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
    } catch {
      return;
    }

    let logged = false;

    const logSnapshot = () => {
      if (logged) return;
      logged = true;

      try {
        sessionStorage.setItem(SESSION_KEY, '1');
      } catch {
        return;
      }

      const cloudFavorites = filterCloudSambandFavorites(readFavoritesCache());
      const legacyRoutes = readLegacyRouteFavorites();
      const mergedCount = mergeSambandFavoritesForRender(cloudFavorites, legacyRoutes).length;

      trackEvent('favorites_inventory_snapshot', {
        local_samband: readLocalSambandCount(),
        cloud_samband: cloudFavorites.length,
        merged_samband: mergedCount,
        has_dock: readDockFavoriteExists(),
        commuter_routes: readCommuterRouteCount(),
      });
    };

    window.addEventListener(FERRY_FAVORITES_UPDATED_EVENT, logSnapshot);
    const timeout = setTimeout(logSnapshot, LOG_DEFER_MS);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener(FERRY_FAVORITES_UPDATED_EVENT, logSnapshot);
    };
  }, []);
}
